import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../../../util/reactIntl';
import clsx from 'clsx';

import { useElementSize } from '@mantine/hooks';
import breakpoints from '../../../../styles/globals.module.scss';
import {
  TourlyResponsiveImage,
  StandardDPRVariants,
  processVariantsForImage,
  getTourlyImageServer,
  CARD_SIZES,
} from '../../../../util/responsiveImagesHelper';

import { NamedLink } from '../../../../components';

import css from './SectionLocations.module.css';

const boatTours = 'catamaran.png';
const roadExcursions= 'road-excursions.jpg';
const hiking= 'levadas.jpeg';
const offroad= 'offroad.jpeg';
const cultureMuseums= 'culture-museums.jpeg';
const surfBodyboard= 'surf-bodyboard.jpg';
const gardensReserves= 'gardens-reserves.jpg';
const scubaSnorkeling= 'scuba-snorkeling.jpg';
const gastronomyDrinks= 'gastronomy-drinks.jpg';

const CategoryImage = (props) => {
  const { image, alt, imageWidth, imageHeight} = props;

  const processedVariants = processVariantsForImage(
    false, // do not proxy image
    imageWidth,
    imageHeight,
    getTourlyImageServer().toString(),
    image,
    StandardDPRVariants
  );

  const allParams = '&blend-alpha=100' + '&fit=crop&crop=edges';

  return <TourlyResponsiveImage
    alt={""}
    image={processedVariants}
    variants={['x1', 'x2']}
    sizes={CARD_SIZES}
    fetchpriority={'low'}
    additionalParams={allParams}
  />;
};

const locationLink = (name, image, imageWidth, imageHeight, searchQuery) => {
  const nameText = <span className={css.locationName}>{name}</span>;
  return (
    <NamedLink name="SearchPage" to={{ search: searchQuery }} className={css.location}>
      <div className={css.imageWrapper}>
        <div className={css.aspectWrapper}>
          <div className={css.locationImage}>
            <CategoryImage
              image={image}
              alt={name}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
            />
          </div>
        </div>
      </div>
      <div className={css.linkText}>
        <FormattedMessage
          id="SectionLocationCategory.listingsCategory"
          values={{ category: nameText }}
        />
      </div>
    </NamedLink>
  );
};

const SectionLocationCategory = (props) => {
        const { rootClassName, className, location: { pathname }} = props;

        const classes = clsx(rootClassName || css.root, className);
        const titleId = pathname === '/home' ? "SectionLocationCategory.title" : "SectionLocationsTO.title";
        const mainLocation = 'Portugal&bounds=42.154276%2C-6.189158%2C29.9501178%2C-31.3687699';

        const { ref, width: categoryPanelWidth } = useElementSize();
        const [categoryImageWidth, setCategoryImageWidth] = useState(0);
        const [categoryImageHeight, setCategoryImageHeight] = useState(0);

        useEffect(() => {
          if (categoryPanelWidth > 0) {
            let width = 0;
            let aspectRatio = 0;
            // show one image only
            if (categoryPanelWidth < breakpoints.viewportMediumPixels) {
              width = categoryPanelWidth;
              aspectRatio = 6 / 13; 
            }
            else { // large screens >= 768, show 3 images
              width = Math.round((categoryPanelWidth-80) / 3)
              aspectRatio = 2 / 3;
            }
            setCategoryImageWidth(width);
            setCategoryImageHeight(Math.round(width * aspectRatio));
          }
        }, [categoryPanelWidth]);

        return (
          <div className={classes} ref={ref}>
            {categoryPanelWidth > 0 ? (
              <>
                <div className={css.title}>
                  <FormattedMessage id={titleId} />
                </div>
                <div className={css.locations}>
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.boatTours" />,
                    boatTours,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Aboattours`
                  )}
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.roadExcursions" />,
                    roadExcursions,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Aroadexcursions`
                  )}
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.hiking" />,
                    hiking,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Ahiking`
                  )}
                </div>
                <div className={css.locations}>
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.offroad" />,
                    offroad,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Aoffroad`
                  )}
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.cultureMuseums" />,
                    cultureMuseums,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Aculturemuseums`
                  )}
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.surfBodyboard" />,
                    surfBodyboard,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Asurfbodyboard`
                  )}
                </div>
                <div className={css.locations}>
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.gardensReserves" />,
                    gardensReserves,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Agardensreserves`
                  )}
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.scubaSnorkeling" />,
                    scubaSnorkeling,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Ascubasnorkeling`
                  )}
                  {locationLink(
                    <FormattedMessage id="SectionLocationCategory.gastronomyDrinks" />,
                    gastronomyDrinks,
                    categoryImageWidth,
                    categoryImageHeight,
                    `?address=${mainLocation}&pub_tourlyCategories=has_any%3Agastronomydrinks`
                  )}
                </div>
              </>
            ) : null}
          </div>
        );
      };;

SectionLocationCategory.defaultProps = { rootClassName: null, className: null, titleId:null };

const { string } = PropTypes;

SectionLocationCategory.propTypes = {
  rootClassName: string,
  className: string,
  titleId: string
};

export default SectionLocationCategory;
